import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import InputText from "../../../../components/form-control/input-text";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from 'primereact/card';
import { Skeleton } from "primereact/skeleton";
import apiFunctions from "../../../../services/api-service";
import useToast from "../../../../components/common/useToast";
import { AxiosError } from "axios";
import { errorHandler } from "../../../../utils/HelperFunctions";
import { persistor } from "../../../../redux/store";
import { logout } from "../../../../redux/features/authSlice";
import { useDispatch } from "react-redux";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

const SubCastCreate: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(!!id);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Name is required!"),
        namegujarati: Yup.string().required("Name in gujarati is required!"),
    });

    const initialValue = {
        name: "",
        namegujarati: ""
    };

    const formik = useFormik({
        initialValues: initialValue,
        validationSchema: validationSchema,

        onSubmit: async (values: any) => {
            console.log(values, "values");
            navigate("/admin/");
            let payload = {
                name: values.name,
                namegujarati: values.namegujarati,
            }
            if (id) {
                await apiFunctions.put(`/subcast/editsubcast/${id}`, payload)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({
                                severity: "success",
                                summary: "Success",
                                detail: response.data.message,
                                life: 3000,
                            });
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) {
                            dispatch(logout());
                            persistor.purge();
                            navigate("/admin/login");
                        }
                        errorHandler(err);
                    })
                    .finally(() => {
                        navigate("/admin/subcast");
                    });
            } else {
                await apiFunctions.post(`/subcast/addsubcast`, payload)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({
                                severity: "success",
                                summary: "Success",
                                detail: response.data.message,
                                life: 3000,
                            });
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) {
                            dispatch(logout());
                            persistor.purge();
                            navigate("/admin/login");
                        }
                        errorHandler(err);
                    })
                    .finally(() => {
                        navigate("/admin/subcast");
                    });
            }
        },
    });

    const convertguharaticall = async (value: string) => {
        try {
            if (value.length > 0) {
                let url = `/textmobile/mobile/translate/${value}`;
                await apiFunctions
                    .get(url)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            formik.setFieldValue("namegujarati", response.data.data);
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        errorHandler(err);
                    })
                    .finally(() => {
                        // setLoad(false);
                    });
            } else {
                formik.setFieldValue("namegujarati", "");
            }
        } catch (error) {
            console.log(error);
        }
    }

    const handleNameChange = (value: string) => {
        formik.setFieldValue("name", value);
        if (value.length > 0) {
            convertguharaticall(value);
        } else {
            formik.setFieldValue("namegujarati", "");
        }
    }

    const getSubcastData = async (id: string) => {
        try {
            let url = `/subcast/${id}`;
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        formik.setFieldValue("name", response.data.data.name);
                        formik.setFieldValue("namegujarati", response.data.data.namegujarati);
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (id) {
            getSubcastData(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBack = () => {
        navigate('/admin/subcast');
    }

    return (
        <>
            <div className="back-button" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>
            <div className="page-title">
                {id ? (<h3>Edit Sub cast</h3>) : (<h3>Add Sub cast</h3>)}
            </div>
            <form action="submit" onSubmit={formik.handleSubmit}>
                <Card className="grid form-layout">
                    {loading ? (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="80px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="120px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="field mt-5 mb-4">
                                    <Skeleton width="90px" height="40px" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <InputText
                                        type="text"
                                        label="Name"
                                        placeholder=""
                                        modelValue={formik.values.name}
                                        disabled={false}
                                        autofocus={true}
                                        id="name"
                                        validation={
                                            formik.errors.name &&
                                            formik.touched.name && {
                                                $error: true,
                                                required: {
                                                    $invalid: true,
                                                    $message: formik.errors.name,
                                                },
                                            }
                                        }
                                        onUpdateModelValue={handleNameChange}
                                    />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <InputText
                                        type="text"
                                        label="Name in Gujarati"
                                        placeholder=""
                                        modelValue={formik.values.namegujarati}
                                        disabled={false}
                                        autofocus={false}
                                        id="namegujarati"
                                        validation={
                                            formik.errors.namegujarati &&
                                            formik.touched.namegujarati && {
                                                $error: true,
                                                required: {
                                                    $invalid: true,
                                                    $message: formik.errors.namegujarati,
                                                },
                                            }
                                        }
                                        onUpdateModelValue={function (value: string): void {
                                            formik.setFieldValue("namegujarati", value);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="field mt-5 mb-4">
                                    {id ? (
                                        <Button
                                            label="Edit"
                                            className="prime-button btn-login w-max"
                                            type="submit"
                                        ></Button>
                                    ) : (
                                        <Button
                                            label="ADD"
                                            className="prime-button btn-login w-max"
                                            type="submit"
                                        ></Button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </Card>
            </form >
        </>
    );
};

export default SubCastCreate;
